import { FunctionFactory } from "survey-core";
import { Goals } from "../quiz/goals";
import { Gender } from "../quiz/gender";
import { getTestimonialByName, TestimonialKey } from "./testimonials";
import { Physiology } from "../quiz/physiology";
import { getGenderKey } from "../features/utils";
import { tSurvey } from "../i18n/survey";
import { locHtml } from "./locHtml";
import { makeSplashHeader } from "./SplashHeader";

const goalTargetSplashHtml = locHtml((t) => `
<div class="new-quote-container">
  <div class="splash-header">
    <h1>${t("splash.goalTarget.title")}</h1>
    <div class="quote">{goalMuscleGainQuote} {goalGenderText}</div>
  </div>
  <div class="quote-img-container">
    <img class="testimonial-image" src="{goalGenderImageUrl}" alt="${t("splash.testimonialAlt")}"/>
  </div>
  <div class="small-text">{goalGenderName}</div>
</div>`);

const goalTargetSplashExpression = {
  name: "goalMuscleGainQuote",
  expression: "renderGoalTargetSplash({goals})",
};

const muscleGainImageUrlMap = {
  [Goals.LoseFat]: {
    [Gender.Male]: getTestimonialByName("Cesar"),
    [Gender.Female]: getTestimonialByName("Katy"),
  },
  [Goals.BuildMuscle]: {
    [Gender.Male]: getTestimonialByName("Abdullah"),
    [Gender.Female]: getTestimonialByName("Tahnee"),
  },
  [Goals.BuildMuscleAndLoseFat]: {
    [Gender.Male]: getTestimonialByName("Stephen"),
    [Gender.Female]: getTestimonialByName("Salma"),
  },
} as const;

function renderGoalTargetSplash(params: any[]): string {
  if (params === undefined || params === null) return "";
  switch (params[0] as Goals) {
    case Goals.LoseFat:
      return tSurvey("splash.goalTarget.quote.loseWeight");
    case Goals.BuildMuscle:
      return tSurvey("splash.goalTarget.quote.buildMuscle");
    case Goals.BuildMuscleAndLoseFat:
      return tSurvey("splash.goalTarget.quote.recomp"); // we want to change this to 2-3%
  }
}

FunctionFactory.Instance.register(
  "renderGoalTargetSplash",
  renderGoalTargetSplash
);

const extractParamValue = (params: any[], prop: TestimonialKey): string => {
  if (params[0] == null || params[1] == null) return "";

  const goals = params[0] as Goals;
  const gender = params[1] as Gender;
  const physiology = params[2] as Physiology;
  const genderKey = getGenderKey(gender, physiology);
  return muscleGainImageUrlMap[goals]?.[genderKey][prop] ?? "";
};

const renderGoalGenderImageUrl = (params: any[]): string =>
  extractParamValue(params, "image");
// The caption above the photo is BWS copy (not the member's quote), so it is
// translated; the English value is the old `testimonial` literal.
const renderGoalGenderText = (params: any[]): string => {
  const name = extractParamValue(params, "name");
  return name ? tSurvey(`splash.goalTarget.caption.${name.toLowerCase()}`) : "";
};
// The whole footnote ("Cesar - Verified Built With Science User"): Spanish
// agrees with the member's gender (Usuario verificado / Usuaria verificada).
const renderGoalGenderName = (params: any[]): string => {
  const name = extractParamValue(params, "displayName");
  if (!name) return "";
  const genderKey = getGenderKey(params[1] as Gender, params[2] as Physiology);
  return tSurvey(
    genderKey === Gender.Female ? "splash.goalTarget.footnote_female" : "splash.goalTarget.footnote_male",
    { name },
  );
};

FunctionFactory.Instance.register(
  "goalGenderImageUrl",
  renderGoalGenderImageUrl
);
FunctionFactory.Instance.register("goalGenderText", renderGoalGenderText);
FunctionFactory.Instance.register("renderGoalGenderName", renderGoalGenderName);

const goalGenderImageUrlExpression = {
  name: "goalGenderImageUrl",
  expression: "goalGenderImageUrl({goals}, {gender}, {physiology})",
};

const goalGenderTextExpression = {
  name: "goalGenderText",
  expression: "goalGenderText({goals}, {gender}, {physiology})",
};

const goalGenderNameExpression = {
  name: "goalGenderName",
  expression: "renderGoalGenderName({goals}, {gender}, {physiology})",
};

const goalTargetSplashElement = {
  name: "goal-target-splash",
  type: "html",
  html: goalTargetSplashHtml,
};

const GoalTargetSplashHeader = makeSplashHeader("splash.headers.goalTarget");

export {
  goalTargetSplashElement,
  goalTargetSplashExpression,
  goalGenderImageUrlExpression,
  goalGenderNameExpression,
  goalGenderTextExpression,
  GoalTargetSplashHeader,
};
